import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import OfferCard from "@/components/OfferCard";
import OilComparisonTable from "@/components/OilComparisonTable";
import { activeSpecials } from "@/data/specials";
import Seo, { breadcrumbLd, offerCatalogLd } from "@/components/seo/Seo";

export default function Specials() {
  const offers = useMemo(activeSpecials, []);
  const brake = offers.find((o) => o.category === "brakes");
  const oils = offers.filter((o) => o.category === "oil");

  // JSON-LD structured data for SEO — one Offer entry per active promotion.
  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "AutoRepair",
    "name": "Auto Basic Care",
    "url": "https://autobasiccare.com",
    "telephone": "+1-205-405-1238",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1700 31st Street SW",
      "addressLocality": "Birmingham",
      "addressRegion": "AL",
      "postalCode": "35221",
      "addressCountry": "US",
    },
    "makesOffer": offers.map((o) => ({
      "@type": "Offer",
      "name": o.title,
      "description": o.description,
      "price": o.price.replace(/[^0-9.]/g, ""),
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "url": "https://autobasiccare.com/specials",
    })),
  }), [offers]);

  return (
    <div className="abc-app">
      <Seo
        path="/specials"
        title="Auto Repair Specials & Promotions — Birmingham AL"
        description="Current oil change, brake, and maintenance specials at Auto Basic Care in Birmingham, AL. Real savings on the services you probably already need."
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Specials", path: "/specials" },
          ]),
          offerCatalogLd(offers),
          jsonLd,
        ]}
      />
      <SiteHeader />

      <section className="financing-hero">
        <div className="container financing-hero-inner">
          <div className="badge">Current Shop Specials</div>
          <h1>Specials & Promotions</h1>
          <p className="financing-hero-p">
            Real savings on the services you probably already need. These are the offers running
            at Auto Basic Care right now — book online or give us a call.
          </p>
          <div className="button-row">
            <Link to="/#schedule" className="btn btn-primary" data-testid="specials-hero-book">
              Schedule Service
            </Link>
            <a href="tel:2054051238" className="btn btn-outline-light" data-testid="specials-hero-call">
              Call 205-405-1238
            </a>
          </div>
        </div>
      </section>

      <main>
        {brake && (
          <section className="section">
            <div className="container">
              <div className="section-header">
                <div>
                  <div className="eyebrow">Brake Service</div>
                  <h2 className="section-title">Stop trouble in its tracks.</h2>
                </div>
              </div>
              <div className="offer-featured-wrap">
                <OfferCard {...brake} variant="featured" testid="offer-brake-featured" />
              </div>
            </div>
          </section>
        )}

        {oils.length > 0 && (
          <section className="section" style={{ background: "var(--gray-soft)" }}>
            <div className="container">
              <div className="section-header">
                <div>
                  <div className="eyebrow">Oil Change Specials</div>
                  <h2 className="section-title">Pick the protection level for your engine.</h2>
                </div>
              </div>
              <div className="offer-grid">
                {oils.map((o) => (
                  <OfferCard key={o.id} {...o} />
                ))}
              </div>

              <div style={{ marginTop: "clamp(28px, 4vw, 44px)" }}>
                <h3 style={{ fontSize: "clamp(20px, 2.2vw, 26px)", margin: "0 0 12px", color: "var(--dark)" }}>
                  Compare tiers at a glance
                </h3>
                <OilComparisonTable />
              </div>
            </div>
          </section>
        )}

        <section className="tires-cta">
          <div className="container">
            <h2>Ready to book?</h2>
            <p>
              Auto Basic Care · 1700 31st Street SW, Birmingham, AL 35221 · Mon–Sat 8:00 AM–4:30 PM.
              Have questions? Call 205-405-1238.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <Link to="/#schedule" className="btn btn-dark" data-testid="specials-cta-book">
                Book Appointment
              </Link>
              <a href="tel:2054051238" className="btn btn-outline-light" data-testid="specials-cta-call">
                Call 205-405-1238
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
